import type { FieldPath, UseFormSetError } from 'react-hook-form';
import { PayloadAction } from '@reduxjs/toolkit';
import axios, { isAxiosError } from 'axios';
import { UNEXPECTED_ERROR } from 'core/constants/error-services.constants';
import type { IServiceError, IServiceErrorContent } from 'core/model/interfaces/service.interface';
import type { TMetaAsyncThunk } from 'core/store/store.helpers';
import { getErrorFromService } from './https';
import { notificationService } from './notification.utils';

const isGeneralError = (error: unknown): error is Error =>
  typeof error === 'object' && error !== null && 'message' in error;

export const handleErrorMessage = (error: unknown, messageError?: string): string => {
  if (axios.isAxiosError<IServiceError>(error)) {
    const serviceError = error.response?.data?.error;

    if (serviceError) {
      return serviceError.message;
    }
  }

  if (isGeneralError(error) && error.message) {
    return error.message;
  }

  if (messageError) {
    return messageError;
  }

  return UNEXPECTED_ERROR;
};

export const handleSpecificErrors = <T>(e: unknown, defaultError: string, MAPPED_ERRORS: Array<T>) => {
  const errorMessage = getErrorFromService(e, defaultError);

  if (isAxiosError(e)) {
    const errorObject = (e.response?.data as IServiceError)?.error;
    if (errorObject) {
      // Handle specific form errors
      if (MAPPED_ERRORS.includes(errorObject.code as T)) {
        return errorObject;
      }
    }
  }
  showNotificationError(errorMessage);
};

export const showNotificationError = (errorMessage: string) => {
  const isDrawerOpened = notificationService.drawer.isAvailable();
  if (isDrawerOpened)
    notificationService.drawer.error(errorMessage, {
      autoHide: true,
      autoHideDuration: 7000,
    });
  else notificationService.global.error(errorMessage);
};

export const getSpecificFieldKey = <K extends string, V extends string>(MAP: Map<K, V>, errorCode: K) => {
  const fieldKey = MAP.get(errorCode);

  if (fieldKey) {
    return fieldKey;
  }

  return null;
};

export const isActionRejected = <T extends PayloadAction<unknown, string, TMetaAsyncThunk>>(resultAction: T) => {
  const isRejected = resultAction.meta.requestStatus === 'rejected';

  return isRejected;
};

export const getSpecifiedErrorKeyByActionRejected = <
  T extends PayloadAction<unknown, string, TMetaAsyncThunk>,
  SpecifiedErrorCodeList extends Map<string, string>,
>(
  resultAction: T,
  MAPPED_ERROR_LIST: SpecifiedErrorCodeList,
) => {
  const isRejected = isActionRejected(resultAction);

  if (isRejected && resultAction.payload) {
    const errorCode = resultAction.payload as IServiceErrorContent<string>;

    const specifiedError = getSpecificFieldKey(MAPPED_ERROR_LIST, errorCode.code);

    return specifiedError;
  }

  return null;
};

export const getSpecifiedCustomError = <
  ActionResult extends PayloadAction<unknown, string, TMetaAsyncThunk>,
  K extends string,
  V extends string,
>(
  resultAction: ActionResult,
  MAP: Map<K, V>,
) => {
  const fieldKey = getSpecifiedErrorKeyByActionRejected(resultAction, MAP);

  if (fieldKey) {
    const payload = resultAction.payload as IServiceErrorContent<string>;

    const args: Parameters<UseFormSetError<Record<V, unknown>>> = [
      fieldKey as FieldPath<Record<V, unknown>>,
      {
        type: 'custom',
        message: payload.details.join('\n\n'),
      },
    ];

    return args;
  }

  return null;
};

export const getErrorMessageFromAction = (payload: unknown, defaultMessage = 'Error inesperado') => {
  const errorContent = payload as IServiceErrorContent<string>;

  const serviceError = errorContent?.message || defaultMessage;

  const message = typeof payload === 'string' ? payload : serviceError;

  return message;
};
